import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { getCampaignById, updateCampaign } from '../../utils/api/campaignAPI';
import { getPhoneNumberByOrgId } from '../../utils/api/phoneNumberAPI';

function EditCampaignModal({ id, show, onClose, onSuccess }) {
  const orgId = useSelector((state) => state?.orgData?.orgData?.organization?.id);
  const listOfAssistants = useSelector((state) => state.assistants.value);

  const [formData, setFormData] = useState({
    name: '',
    assistantId: '',
    phoneNumberId: '',
    scheduleType: 'now',
    scheduleDateTime: '',
  });

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  // Fetch campaign data and phone numbers when modal opens
  useEffect(() => {
    if (!show || !id) return;

    const fetchData = async () => {
      setLoading(true);
      setLoadingPhoneNumbers(true);

      try {
        // Fetch campaign data
        const campaignRes = await getCampaignById(id);
        const campaign = campaignRes?.campaign;

        if (campaign) {
          // Check if campaign is already completed (scheduled time has passed)
          const hasSchedule = campaign.schedulePlan?.earliestAt;
          if (hasSchedule) {
            const scheduleDate = new Date(campaign.schedulePlan.earliestAt);
            const now = new Date();
            
            // If scheduled time has passed, prevent editing
            if (scheduleDate < now) {
              toast.error('Completed campaign cannot be edited');
              onClose();
              return;
            }
          }

          // Determine schedule type
          const scheduleType = hasSchedule ? 'later' : 'now';
          
          // Format schedule date for input
          let scheduleDateTime = '';
          if (hasSchedule) {
            const scheduleDate = new Date(campaign.schedulePlan.earliestAt);
            const year = scheduleDate.getFullYear();
            const month = String(scheduleDate.getMonth() + 1).padStart(2, '0');
            const day = String(scheduleDate.getDate()).padStart(2, '0');
            const hours = String(scheduleDate.getHours()).padStart(2, '0');
            const minutes = String(scheduleDate.getMinutes()).padStart(2, '0');
            scheduleDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
          }

          setFormData({
            name: campaign.name || '',
            assistantId: campaign.assistantId || '',
            phoneNumberId: campaign.phoneNumberId || '',
            scheduleType,
            scheduleDateTime,
          });
        }

        // Fetch phone numbers
        if (orgId) {
          const phoneRes = await getPhoneNumberByOrgId(orgId);
          const sortedNumbers = (phoneRes.phoneNumbers || []).sort((a, b) =>
            a.did.localeCompare(b.did)
          );
          setPhoneNumbers(sortedNumbers);
        }
      } catch (e) {
        console.error('Error fetching data:', e);
        toast.error('Failed to load campaign data');
        onClose();
      } finally {
        setLoading(false);
        setLoadingPhoneNumbers(false);
      }
    };

    fetchData();
  }, [show, id, orgId, onClose]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        name: '',
        assistantId: '',
        phoneNumberId: '',
        scheduleType: 'now',
        scheduleDateTime: '',
      });
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSubmitFormData = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.assistantId || !formData.phoneNumberId) {
      toast.error('Please fill all required fields.');
      return;
    }

    if (formData.scheduleType === 'later' && !formData.scheduleDateTime) {
      toast.error('Please select a schedule date and time.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        assistantId: formData.assistantId,
        phoneNumberId: formData.phoneNumberId,
      };

      if (formData.scheduleType === 'later' && formData.scheduleDateTime) {
        const scheduleDate = new Date(formData.scheduleDateTime);
        payload.schedulePlan = {
          earliestAt: scheduleDate.toISOString(),
        };
      }

      const res = await updateCampaign(id, payload);
      toast.success(res?.message || 'Campaign updated successfully!');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error updating campaign:", err);
      toast.error(err?.message || 'Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '800px' }}>
        <div className="modal-content" style={{
          borderRadius: '16px',
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
          <div className="modal-header">
            <h5 className="modal-title">Edit Campaign</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmitFormData}>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="shimmer shimmer-text"
                      style={{ width: '120px', height: '2.5rem', margin: '10px auto' }}
                    />
                  ))}
                </div>
              ) : (
                <div className="row g-3">
                  {/* Campaign Name */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">Campaign Name*</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Summer Sales Campaign"
                    />
                  </div>

                  {/* Assistant Selection */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Assistant*</label>
                    <div className="position-relative">
                      <Icon
                        icon="lucide:bot"
                        width="18"
                        height="18"
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6b7280',
                          zIndex: 1,
                        }}
                      />
                      <select
                        name="assistantId"
                        className="form-control"
                        value={formData.assistantId}
                        onChange={handleChange}
                        required
                        style={{ paddingLeft: '40px' }}
                      >
                        <option value="">Select Assistant</option>
                        {listOfAssistants.map((assistant) => (
                          <option key={assistant.id} value={assistant.id}>
                            {assistant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Phone Number Selection */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Phone Number*</label>
                    <div className="position-relative">
                      <Icon
                        icon="lucide:phone"
                        width="18"
                        height="18"
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          color: '#6b7280',
                          zIndex: 1,
                        }}
                      />
                      <select
                        name="phoneNumberId"
                        className="form-control"
                        value={formData.phoneNumberId}
                        onChange={handleChange}
                        required
                        disabled={loadingPhoneNumbers}
                        style={{ paddingLeft: '40px' }}
                      >
                        <option value="">{loadingPhoneNumbers ? 'Loading...' : 'Select Phone Number'}</option>
                        {phoneNumbers.map((phone) => (
                          <option key={phone.id} value={phone.id}>
                            {phone.did}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Schedule Type */}
                  <div className="col-12">
                    <label className="form-label fw-semibold">Schedule*</label>
                    <div className="d-flex align-items-center justify-content-between" style={{ maxWidth: '300px', margin: '0 auto' }}>
                      <div className="form-check d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="scheduleType"
                          id="editScheduleNow"
                          value="now"
                          checked={formData.scheduleType === 'now'}
                          onChange={handleChange}
                          style={{ marginTop: 0 }}
                        />
                        <label className="form-check-label mb-0" htmlFor="editScheduleNow">
                          Send Now
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="scheduleType"
                          id="editScheduleLater"
                          value="later"
                          checked={formData.scheduleType === 'later'}
                          onChange={handleChange}
                          style={{ marginTop: 0 }}
                        />
                        <label className="form-check-label mb-0" htmlFor="editScheduleLater">
                          Schedule Later
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Schedule DateTime */}
                  {formData.scheduleType === 'later' && (
                    <div className="col-12">
                      <label className="form-label fw-semibold">Schedule Date & Time*</label>
                      <input
                        type="datetime-local"
                        name="scheduleDateTime"
                        className="form-control"
                        value={formData.scheduleDateTime}
                        onChange={handleChange}
                        min={getMinDateTime()}
                        required
                      />
                      <small className="text-muted">
                        Minimum: 5 minutes from now
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={saving || loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || loading}
              >
                {saving ? 'Updating...' : 'Update Campaign'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}

export default EditCampaignModal;

